export type ItemOffset = { x: number; y: number };

const ARC_BOTTOM = Math.PI / 2;
const ARC_BOTTOM_START = (3 * Math.PI) / 4;
const ARC_BOTTOM_END = Math.PI / 4;

function getArcAngles(count: number): number[] {
    if (count <= 0) {
        return [];
    }

    if (count === 1) {
        return [ARC_BOTTOM];
    }

    return Array.from({ length: count }, (_, index) => {
        const t = index / (count - 1);

        return ARC_BOTTOM_START + t * (ARC_BOTTOM_END - ARC_BOTTOM_START);
    });
}

function getPointOnOrbit(cx: number, cy: number, rx: number, ry: number, angle: number) {
    return {
        x: cx + rx * Math.cos(angle),
        y: cy + ry * Math.sin(angle),
    };
}

function getPictogramCenterInParent(pictogramEl: HTMLElement, parentEl: HTMLElement) {
    let x = 0;
    let y = 0;
    let el: HTMLElement | null = pictogramEl;

    while (el && el !== parentEl) {
        if (el === pictogramEl) {
            x += el.offsetLeft + el.offsetWidth / 2;
            y += el.offsetTop + el.offsetHeight / 2;
        } else {
            x += el.offsetLeft;
            y += el.offsetTop;
        }

        el = el.offsetParent as HTMLElement | null;
    }

    if (el === parentEl) {
        return { x, y };
    }

    const parentRect = parentEl.getBoundingClientRect();
    const pictogramRect = pictogramEl.getBoundingClientRect();

    return {
        x: pictogramRect.left + pictogramRect.width / 2 - parentRect.left,
        y: pictogramRect.top + pictogramRect.height / 2 - parentRect.top,
    };
}

export function measureItemArcOffsets(
    bottomEl: HTMLElement,
    orbitEl: HTMLElement,
    pictogramEls: (HTMLElement | null)[],
): ItemOffset[] {
    const bottomRect = bottomEl.getBoundingClientRect();
    const orbitRect = orbitEl.getBoundingClientRect();
    const cx = orbitRect.left + orbitRect.width / 2 - bottomRect.left;
    const cy = orbitRect.top + orbitRect.height / 2 - bottomRect.top;
    const rx = orbitRect.width / 2;
    const ry = orbitRect.height / 2;

    if (rx <= 0 || ry <= 0) {
        return pictogramEls.map(() => ({ x: 0, y: 0 }));
    }

    const arcAngles = getArcAngles(pictogramEls.length);

    return pictogramEls.map((pictogramEl, index) => {
        if (!pictogramEl) {
            return { x: 0, y: 0 };
        }

        const { x: defaultX, y: defaultY } = getPictogramCenterInParent(pictogramEl, bottomEl);
        const arcPoint = getPointOnOrbit(cx, cy, rx, ry, arcAngles[index] ?? ARC_BOTTOM);

        if (pictogramEls.length === 1) {
            return {
                x: 0,
                y: arcPoint.y - defaultY,
            };
        }

        return {
            x: arcPoint.x - defaultX,
            y: arcPoint.y - defaultY,
        };
    });
}
